import {Link} from 'react-router';
import {groupBy} from 'lodash';
import {
    Button,
    FormControl,
    FormSelect,
    Modal,
    OverlayTrigger,
    Tooltip,
} from '@goproblems/ui';

import {hideModal, useSelector} from '@utils';
import {MODAL_PROBLEM_FLAGS} from '@utils';
import {useTranslation} from 'react-i18next';
import {Loading, ProblemFlagBadge, ProblemFlagsTable} from '@components';
import {ProblemFlagEnum} from '@types';
import {useAddProblemFlagMutation, useFetchProblemFlagsQuery} from '@api';
import {useState} from 'react';
import {toast} from 'react-toastify';
import {isMobile} from 'react-device-detect';

export const ProblemFlagsModal = ({
    problemId,
    name = MODAL_PROBLEM_FLAGS,
    afterDelete,
    afterSubmit,
}: {
    problemId: number;
    name?: string;
    afterSubmit?: () => void;
    afterDelete?: () => void;
}) => {
    const {user} = useSelector(s => s.auth);
    const show = useSelector(s => s.modalProblemFlags);
    const {t} = useTranslation();
    const {
        data: flagsData,
        isLoading,
        refetch,
    } = useFetchProblemFlagsQuery(
        {problemId},
        {
            refetchOnMountOrArgChange: true,
            skip: !show,
        }
    );
    const [addProblemFlag] = useAddProblemFlagMutation();
    const [type, setType] = useState<number>(ProblemFlagEnum.WELL_CONSTRUCTED);
    const [comment, setComment] = useState<string>();
    const [isInvalid, setIsInvalid] = useState<boolean>(false);

    const handleClose = () => {
        hideModal(name);
    };

    const group = groupBy(flagsData?.entries, 'type');
    const badges = Object.entries(group).map(([key, value]) => (
        <ProblemFlagBadge key={key} type={key} number={value.length} />
    ));

    const flagged =
        user && flagsData?.entries?.find(flag => flag.user === user.name)
            ? true
            : false;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName={`max-w-fit ${isMobile ? '' : 'min-w-[768px]'}`}
            scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{t('flags.title')}</Modal.Title>
                {
                    <Link
                        className="mt-2 ml-2 link text-xs"
                        to={`/problems/${problemId}/flaghist`}>
                        {t('single_problem.change_history')}
                    </Link>
                }
            </Modal.Header>
            <Modal.Body>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        flagged ? (
                            <Tooltip>
                                {t('flags.already_flagged')}
                            </Tooltip>
                        ) : (
                            <></>
                        )
                    }>
                    <div className="flex gap-4">
                        <div className="d-block w-full">
                            <FormSelect
                                defaultValue={type}
                                disabled={flagged}
                                onChange={e => {
                                    setType(parseInt(e.target.value));
                                    setIsInvalid(false);
                                    setComment('');
                                }}>
                                <option
                                    value={ProblemFlagEnum.WELL_CONSTRUCTED}>
                                    {t('flags.well_constructed')}
                                </option>
                                <option value={ProblemFlagEnum.NEEDS_FIXING}>
                                    {t('flags.needs_fixing')}
                                </option>
                                <option value={ProblemFlagEnum.INVALID}>
                                    {t('flags.invalid')}
                                </option>
                                <option value={ProblemFlagEnum.DUPLICATE}>
                                    {t('flags.duplicate')}
                                </option>
                            </FormSelect>
                        </div>
                        {type !== ProblemFlagEnum.WELL_CONSTRUCTED && (
                            <div>
                                <FormControl
                                    className="min-w-72"
                                    placeholder={t('flags.description_required')}
                                    disabled={flagged}
                                    value={comment}
                                    onChange={e => {
                                        setIsInvalid(false);
                                        setComment(e.target.value);
                                    }}
                                    isInvalid={isInvalid}
                                />
                                <FormControl.Feedback
                                    type="invalid"
                                    className="absolute">
                                    {t('flags.description_is_required')}
                                </FormControl.Feedback>
                            </div>
                        )}
                        <Button
                            variant="primary"
                            disabled={flagged}
                            onClick={async () => {
                                if (
                                    type !== ProblemFlagEnum.WELL_CONSTRUCTED &&
                                    !comment
                                ) {
                                    setIsInvalid(true);
                                } else {
                                    try {
                                        await addProblemFlag({
                                            problemId,
                                            type,
                                            comment:
                                                type ===
                                                ProblemFlagEnum.WELL_CONSTRUCTED
                                                    ? ''
                                                    : comment,
                                        }).unwrap();
                                        toast.success(t('flags.added'), {
                                            autoClose: 1000,
                                        });
                                        void refetch();
                                        if (afterSubmit) {
                                            afterSubmit();
                                        }
                                    } catch (error: any) {
                                        toast.error(error.data.detail);
                                    }
                                }
                            }}>
                            {t('common.submit')}
                        </Button>
                    </div>
                </OverlayTrigger>
                <div className={isInvalid ? 'my-8' : 'my-4'}>{badges}</div>
                <div>
                    {isLoading && <Loading />}
                    {!isLoading && flagsData?.entries && (
                        <ProblemFlagsTable
                            flags={flagsData.entries}
                            onDelete={() => {
                                void refetch();
                                if (afterDelete) afterDelete();
                            }}
                        />
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t('common.close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
